/**
 * D-EZ-108 — THE RIVALRY ENGINE, WITH MEMORY.
 *
 * Heat between two drivers is built ONLY from incidents the dice produced
 * (`memory.ts` lists them), and it cools: a little after every race of the
 * league, a lot over a winter. Every incident is remembered — season, race,
 * track, turn, who did what — so a rivalry has a first spark, a worst moment and
 * a last word, and the booth can say "Ackerd wrecked him here last spring"
 * because the record says so.
 *
 * ⚠ NARRATIVE ONLY (THE GOVERNING RULE, D-EZ-104…111). Nothing here is read by
 * the engine, nothing here draws a die, and nothing here changes a result. It is
 * a pure fold over the archived seasons and the one being raced — derivable, so
 * nothing new is stored except the archive's few-hundred-byte digest of each
 * race's tape (`RaceMemory`), which exists only because the archive throws the
 * tape away.
 *
 * Also here, because they are the same fold: friendships between teammates who
 * keep finishing together, and mentor/protégé pairs (a veteran and a rookie in
 * the same shop). Low heat, good memories.
 */

import type { SeasonRaceResult, SeasonState } from '../seasonPlay.ts';
import { liveHistorySeason, sortedSeasons, type HistorySeason } from '../history/source.ts';
import { ageOf } from '../aging.ts';
import { raceMemoryOf, type Incident, type IncidentKind } from './memory.ts';

// ---------------------------------------------------------------------------
// Tuning — measured, see `rivalry.measure.test.ts` and the report in
// CONTINUATION.md. Change these and re-measure.
// ---------------------------------------------------------------------------

export interface RivalryTuning {
  /** Heat kept after each league race. */
  raceDecay: number;
  /** Heat kept over a winter. */
  winterDecay: number;
  /** Bond kept after each race / over a winter. */
  bondDecay: number;
  bondWinterDecay: number;
  simmering: number;
  hot: number;
  boiling: number;
  /** Heat each kind of incident adds. */
  heat: Record<IncidentKind | 'feud-live', number>;
}

export const RIVALRY_TUNING: RivalryTuning = {
  raceDecay: 0.96,
  winterDecay: 0.6,
  bondDecay: 0.97,
  bondWinterDecay: 0.75,
  simmering: 11,
  hot: 17,
  boiling: 28,
  heat: {
    wrecked: 12,
    duel: 2.5,
    'duel-failed': 1.5,
    'feud-flare': 5,
    'shared-wreck': 2,
    battle: 2,
    'lead-pass': 1,
    'photo-finish': 6,
    'late-pass-win': 6,
    'feud-start': 8,
    'feud-live': 1.5,
    words: 4,
    hatchet: 0,
    seat: 6,
  },
};

/** Heat kept when the printed rules end a feud (the chart, or RB/xvi's roll). */
const HATCHET_KEEP = 0.4;

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export type RivalryStateName = 'boiling' | 'hot' | 'simmering' | 'cooled' | 'buried-the-hatchet' | 'dormant';

/** One remembered moment between two drivers. */
export interface RivalryMoment {
  kind: IncidentKind;
  seasonNumber: number;
  /** 0 = over the winter before the season. */
  raceNumber: number;
  /** A running count across the league, 1-based; 0.5 steps for a winter. */
  leagueRaceIndex: number;
  raceName: string;
  trackName: string;
  /** The physical track, the same across card years. */
  venue: string;
  turn: number | null;
  /** The man who did it, when somebody did. */
  aggressorId: string | null;
  victimId: string | null;
  /** The heat it added (positive) at the time. */
  heat: number;
  /** One line, plain racing language. */
  text: string;
  /** The incident's own detail ('out', 'self-out', 'win', 'pit', a battle's count…). */
  detail: string | null;
}

export interface HeadToHead {
  /** Races both started. */
  races: number;
  /** Races `a` finished ahead of `b`, and the other way round. */
  aAhead: number;
  bAhead: number;
}

export interface RivalryRecord {
  /** Sorted ids: a < b. */
  a: string;
  b: string;
  aName: string;
  bName: string;
  /** Heat now, one decimal. */
  heat: number;
  peak: number;
  peakAt: { seasonNumber: number; raceNumber: number } | null;
  state: RivalryStateName;
  /** The first moment that put heat between them. */
  firstSpark: RivalryMoment | null;
  /** The moment that added the most heat. */
  worst: RivalryMoment | null;
  /** Every moment, oldest first. */
  moments: RivalryMoment[];
  /** When the printed rules last ended a feud between them, if they did. */
  hatchet: RivalryMoment | null;
  headToHead: HeadToHead;
  /** Both still licensed in the latest season read. */
  bothActive: boolean;
  /** Teammates in the latest season read. */
  teammates: boolean;
  /** Rising: heat added in the last three races. Falling: nothing in the last six. */
  trend: 'rising' | 'falling' | 'steady';
}

export type BondKind = 'mentor' | 'teammates' | 'friends';

export interface Bond {
  a: string;
  b: string;
  aName: string;
  bName: string;
  kind: BondKind;
  /** The veteran, for a mentor bond. */
  mentorId: string | null;
  /** Good memories, decayed like heat. */
  warmth: number;
  /** Seasons in the same shop. */
  seasonsTogether: number[];
  /** Races they finished side by side in the top ten. */
  sideBySide: number;
  since: number;
  /** Still in the same shop in the latest season read. */
  current: boolean;
}

export interface RivalryBook {
  asOf: { seasonNumber: number; raceNumber: number } | null;
  /** Every pair that ever simmered or worse, hottest first; dormant pairs are left out. */
  rivalries: RivalryRecord[];
  bonds: Bond[];
  /** Every pair with any memory, by sorted key — including dormant ones. */
  byPair: ReadonlyMap<string, RivalryRecord>;
  /** Every moment, by `${seasonNumber}|${raceNumber}` (raceNumber 0 = the winter). */
  momentsByRace: ReadonlyMap<string, RivalryMoment[]>;
  /** Present when `measure: true` — one row per league race. */
  timeline?: { seasonNumber: number; raceNumber: number; boiling: number; hot: number; simmering: number }[];
  /** Completed spans at simmering or above, for measurement (`measure: true`). */
  spans?: { a: string; b: string; from: number; to: number; peak: number }[];
}

export interface LiveLeagueLike {
  seasonNumber: number;
  roster: HistorySeason['roster'];
  teams: HistorySeason['teams'];
  state: SeasonState;
}

export interface RivalryInput {
  /** Settled seasons of this league, any order. */
  archive?: readonly HistorySeason[];
  /** The season being raced, as `HistorySeason` or as the shell holds it. */
  live?: HistorySeason | LiveLeagueLike | null;
  /** Read nothing after this race (inclusive). */
  throughRace?: { seasonNumber: number; raceNumber: number } | null;
  tuning?: Partial<RivalryTuning>;
  /** Keep a per-race timeline and the closed spans (measurement only). */
  measure?: boolean;
}

export const pairKey = (a: string, b: string): string => (a < b ? `${a}|${b}` : `${b}|${a}`);

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

function ordinalWord(n: number): string {
  const v = n % 100;
  const s = v >= 11 && v <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${s}`;
}

/** One line for an incident. Only names and the incident's own detail go in. */
export function describeIncident(
  inc: { k: IncidentKind; a: string; b: string; d?: string },
  name: (id: string) => string,
  extra: { team?: string | null } = {},
): string {
  const A = name(inc.a);
  const B = name(inc.b);
  switch (inc.k) {
    case 'wrecked':
      if (inc.d === 'self-out') return `${A} went after ${B} and it was ${A} who was wrecked out.`;
      if (inc.d === 'feud-out') return `Their feud boiled over — ${A} took ${B} out of the race.`;
      if (inc.d === 'fume-out') return `${A} vented on ${B}, and ${B} was wrecked out of the race.`;
      if (inc.d === 'out') return `${A} beat ${B} in a duel and put him in the wall — his day was over.`;
      return `${A} beat ${B} in a duel and put him in the wall.`;
    case 'duel':
      return `${A} beat ${B} in a duel and left him in trouble; ${B} got through it.`;
    case 'duel-failed':
      if (inc.d === 'self-out') return `${A} went after ${B} in a duel, got it wrong and wrecked out.`;
      if (inc.d === 'self') return `${A} went after ${B} in a duel, got it wrong and hit the wall.`;
      return `${A} went after ${B} in a duel and got it wrong.`;
    case 'feud-flare':
      return inc.d === 'fume' ? `${A} vented his temper on ${B} on the track.` : `The feud between ${A} and ${B} flared up on the track.`;
    case 'shared-wreck':
      return `${A} and ${B} were collected in the same crash.`;
    case 'battle':
      return `${A} and ${B} went at each other ${inc.d ?? 'again and again'} times for the same piece of track.`;
    case 'lead-pass':
      return inc.d === 'pit' ? `${A} beat ${B} off pit road and took the lead.` : `${A} took the lead from ${B}.`;
    case 'photo-finish':
      if (inc.d === 'win') return `${A} beat ${B} to the line for the win.`;
      if (inc.d === 'held') return `${A} held off ${B} at the line for the win.`;
      return `${A} beat ${B} to the line${inc.d ? ` for ${ordinalWord(Number(inc.d))}` : ''}.`;
    case 'late-pass-win':
      return `${A} took the lead from ${B} late and won it.`;
    case 'feud-start':
      return `A feud between ${A} and ${B} went on the list.`;
    case 'words':
      return `Their feud was the talk of ${A}'s post-race interview.`;
    case 'hatchet':
      return inc.d === 'interview' ? `The post-race interview ended their feud.` : `Their feud cooled off.`;
    case 'seat':
      return extra.team ? `${A} took over the ${extra.team} ride ${B} had.` : `${A} took over the ride ${B} had.`;
  }
}

// ---------------------------------------------------------------------------
// The fold
// ---------------------------------------------------------------------------

/**
 * ⚠ HEAT IS STORED SCALED. Decaying every pair after every race is O(pairs ×
 * races) and a ten-season league has thousands of pairs. Instead one league-wide
 * factor `G` is decayed, a pair stores heat / G at the time it was added, and
 * the real heat is `heat × G` — the same number, computed once at the end.
 */
interface Pair {
  a: string;
  b: string;
  /** Heat ÷ G. */
  heat: number;
  peak: number;
  peakAt: { seasonNumber: number; raceNumber: number } | null;
  moments: RivalryMoment[];
  hatchet: RivalryMoment | null;
  spanFrom: number | null;
}

interface BondState {
  a: string;
  b: string;
  /** Warmth ÷ the bond factor. */
  warmth: number;
  seasons: Set<number>;
  sideBySide: number;
  mentorId: string | null;
  since: number;
}

function isLive(x: HistorySeason | LiveLeagueLike): x is LiveLeagueLike {
  return 'state' in x && !('season' in x);
}

/** Every season the input names, settled ones winning over a live one of the same number. */
export function rivalrySeasons(input: Pick<RivalryInput, 'archive' | 'live'>): HistorySeason[] {
  const byNumber = new Map<number, HistorySeason>();
  if (input.live) {
    const live = isLive(input.live) ? liveHistorySeason(input.live) : input.live;
    byNumber.set(live.seasonNumber, live);
  }
  for (const s of input.archive ?? []) {
    const existing = byNumber.get(s.seasonNumber);
    // A settled season beats the live one of the same year.
    if (!existing || existing.complete === false) byNumber.set(s.seasonNumber, s);
  }
  return sortedSeasons([...byNumber.values()]);
}

const normTeam = (teamId: string): string => teamId.replace(/-s\d+$/, '');

export function rivalryState(input: RivalryInput): RivalryBook {
  const tuning: RivalryTuning = {
    ...RIVALRY_TUNING,
    ...input.tuning,
    heat: { ...RIVALRY_TUNING.heat, ...(input.tuning?.heat ?? {}) },
  };
  const seasons = rivalrySeasons(input);
  const through = input.throughRace ?? null;
  const names = new Map<string, string>();
  for (const s of seasons) {
    for (const c of s.roster) names.set(c.driverId, c.name);
    for (const d of s.season.finalStandings.drivers) names.set(d.driverId, d.name);
    for (const c of s.offseason?.roster ?? []) names.set(c.driverId, c.name);
  }
  const name = (id: string) => names.get(id) ?? id;

  const pairs = new Map<string, Pair>();
  const bonds = new Map<string, BondState>();
  const timeline: NonNullable<RivalryBook['timeline']> = [];
  const spans: NonNullable<RivalryBook['spans']> = [];
  let leagueIndex = 0;
  let asOf: RivalryBook['asOf'] = null;
  let lastSeason: HistorySeason | null = null;
  /** True when `throughRace` stopped the read inside a season (its winter has not happened). */
  let cutInside = false;
  /** Seasons each driver has been seen in, for veteran/rookie. */
  const seenSeasons = new Map<string, number>();
  /** The league-wide decay factors (see `Pair`). */
  let G = 1;
  let Gb = 1;
  const decay = (heatBy: number, bondBy: number) => {
    G *= heatBy;
    Gb *= bondBy;
    // Keep the scaled values well inside a double's range over any league length.
    if (G < 1e-150) {
      for (const p of pairs.values()) p.heat *= G;
      G = 1;
    }
    if (Gb < 1e-150) {
      for (const b of bonds.values()) b.warmth *= Gb;
      Gb = 1;
    }
  };
  const notePeak = (p: Pair, where: { seasonNumber: number; raceNumber: number }) => {
    const now = p.heat * G;
    if (now > p.peak) {
      p.peak = now;
      p.peakAt = { seasonNumber: where.seasonNumber, raceNumber: where.raceNumber };
    }
  };

  const pairOf = (a: string, b: string): Pair => {
    const k = pairKey(a, b);
    let p = pairs.get(k);
    if (!p) {
      const [x, y] = a < b ? [a, b] : [b, a];
      p = { a: x, b: y, heat: 0, peak: 0, peakAt: null, moments: [], hatchet: null, spanFrom: null };
      pairs.set(k, p);
    }
    return p;
  };

  const bondOf = (a: string, b: string, since: number): BondState => {
    const k = pairKey(a, b);
    let s = bonds.get(k);
    if (!s) {
      const [x, y] = a < b ? [a, b] : [b, a];
      s = { a: x, b: y, warmth: 0, seasons: new Set(), sideBySide: 0, mentorId: null, since };
      bonds.set(k, s);
    }
    return s;
  };

  const add = (
    inc: Incident,
    where: { seasonNumber: number; raceNumber: number; raceName: string; trackName: string; venue: string; teamName?: string | null },
    heatOverride?: number,
  ) => {
    if (!inc.a || !inc.b || inc.a === inc.b) return;
    const p = pairOf(inc.a, inc.b);
    const h = heatOverride ?? tuning.heat[inc.k];
    const moment: RivalryMoment = {
      kind: inc.k,
      seasonNumber: where.seasonNumber,
      raceNumber: where.raceNumber,
      leagueRaceIndex: leagueIndex,
      raceName: where.raceName,
      trackName: where.trackName,
      venue: where.venue,
      turn: inc.t,
      aggressorId: AGGRESSIVE.has(inc.k) ? inc.a : null,
      victimId: AGGRESSIVE.has(inc.k) ? inc.b : null,
      heat: Math.round(h * 10) / 10,
      text: describeIncident(inc, name, { team: where.teamName ?? null }),
      detail: inc.d ?? null,
    };
    if (inc.k === 'hatchet') {
      p.heat *= HATCHET_KEEP;
      p.hatchet = moment;
    } else {
      p.heat += h / G;
    }
    p.moments.push(moment);
    notePeak(p, where);
  };

  /** Measurement only: open and close each pair's spans at simmering. */
  const closeSpans = (endIndex: number) => {
    for (const p of pairs.values()) {
      const live = p.heat * G >= tuning.simmering;
      if (live && p.spanFrom === null) p.spanFrom = endIndex;
      else if (!live && p.spanFrom !== null) {
        spans.push({ a: p.a, b: p.b, from: p.spanFrom, to: endIndex, peak: Math.round(p.peak) });
        p.spanFrom = null;
      }
    }
  };

  outer: for (const s of seasons) {
    if (through && s.seasonNumber > through.seasonNumber) break;

    const prevSeason = lastSeason;
    lastSeason = s;
    // ---- the winter before this season -------------------------------------
    if (prevSeason) {
      decay(tuning.winterDecay, tuning.bondWinterDecay);
      const winter = { seasonNumber: s.seasonNumber, raceNumber: 0, raceName: 'The winter', trackName: '', venue: '' };
      // Who took whose ride: a shop's leavers and arrivals, paired in order.
      if (prevSeason.offseason) {
        const before = new Map(prevSeason.teams.map((t) => [normTeam(t.teamId), t]));
        const nextActive = new Set(prevSeason.offseason.roster.map((c) => c.driverId));
        for (const t of s.teams) {
          const old = before.get(normTeam(t.teamId));
          if (!old) continue;
          const left = old.driverIds.filter((id) => !t.driverIds.includes(id));
          const came = t.driverIds.filter((id) => !old.driverIds.includes(id));
          for (let i = 0; i < Math.min(left.length, came.length); i++) {
            // Only a man still racing somewhere has a grievance about it.
            if (!nextActive.has(left[i])) continue;
            add({ k: 'seat', a: came[i], b: left[i], t: null }, { ...winter, teamName: t.name });
          }
        }
      }
    }

    // ---- this season's shops: teammates, mentors ---------------------------
    for (const t of s.teams) {
      const ids = t.driverIds.filter((id) => s.roster.some((c) => c.driverId === id));
      for (let x = 0; x < ids.length; x++) {
        for (let y = x + 1; y < ids.length; y++) {
          const bond = bondOf(ids[x], ids[y], s.seasonNumber);
          bond.seasons.add(s.seasonNumber);
          if (bond.mentorId === null) {
            const vx = veteranRookie(ids[x], ids[y], s, seenSeasons);
            if (vx) {
              bond.mentorId = vx;
              bond.warmth += 4 / Gb;
            }
          }
        }
      }
    }
    for (const c of s.roster) seenSeasons.set(c.driverId, (seenSeasons.get(c.driverId) ?? 0) + 1);

    const teamOf = new Map<string, string>();
    for (const t of s.teams) for (const id of t.driverIds) teamOf.set(id, t.teamId);

    // ---- race by race ------------------------------------------------------
    const races = [...s.season.races].sort((x, y) => x.raceNumber - y.raceNumber);
    for (const run of races) {
      if (through && s.seasonNumber === through.seasonNumber && run.raceNumber > through.raceNumber) {
        cutInside = true;
        break outer;
      }
      leagueIndex += 1;
      decay(tuning.raceDecay, tuning.bondDecay);

      const where = {
        seasonNumber: s.seasonNumber,
        raceNumber: run.raceNumber,
        raceName: run.scheduled.raceName,
        trackName: run.scheduled.trackName,
        venue: run.scheduled.venue ?? run.scheduled.trackId,
      };

      // The tape, or its digest.
      for (const inc of raceMemoryOf(run)?.incidents ?? []) {
        if (inc.k === 'battle') {
          const n = Number(inc.d ?? 3);
          add(inc, where, tuning.heat.battle + Math.min(4, Math.max(0, n - 3)) * 0.5);
        } else if (inc.k === 'wrecked' && (inc.d === 'out' || inc.d === 'feud-out' || inc.d === 'fume-out')) {
          add(inc, where, tuning.heat.wrecked + 3);
        } else {
          add(inc, where);
        }
      }

      // What survives the archive on its own: the line, the late pass.
      for (const inc of finishIncidents(run)) add(inc, where);

      // Feuds on the list (RB/xvi), and the interview chart about them.
      for (const thread of s.season.feudThreads) {
        if (thread.startedRace === run.raceNumber) {
          add({ k: 'feud-start', a: thread.a, b: thread.b, t: null }, where);
        } else if (thread.races.includes(run.raceNumber)) {
          const p = pairOf(thread.a, thread.b);
          p.heat += tuning.heat['feud-live'] / G;
          notePeak(p, where);
        }
        if (thread.endedRace === run.raceNumber && thread.endedBy === 'cooled-off') {
          add({ k: 'hatchet', a: thread.a, b: thread.b, t: null, d: 'cooled' }, where);
        }
      }
      for (const e of run.events ?? []) {
        if (e.eventType !== 'post-race-interview') continue;
        const band = e.momentTag.replace(/^interview\./, '');
        if (band !== 'feud' && band !== 'fume') continue;
        const who = e.actorIds[0];
        if (!who) continue;
        const thread = [...s.season.feudThreads]
          .filter((t) => (t.a === who || t.b === who) && t.races.includes(run.raceNumber))
          .sort((x, y) => y.startedRace - x.startedRace)[0];
        if (!thread) continue;
        const other = thread.a === who ? thread.b : thread.a;
        add({ k: 'words', a: who, b: other, t: null }, where);
        const action = e.modifiers.find((m) => m.name === 'action taken')?.value;
        if (typeof action === 'string' && /feud ends/i.test(action)) {
          add({ k: 'hatchet', a: who, b: other, t: null, d: 'interview' }, where);
        }
      }

      // Teammates who finished side by side in the top ten.
      const order = run.race.finishingOrder;
      for (let i = 0; i + 1 < order.length && i < 10; i++) {
        const x = order[i];
        const y = order[i + 1];
        if (y.place > 10 || x.status !== 'running' || y.status !== 'running') continue;
        const tx = teamOf.get(x.driverId);
        if (!tx || tx !== teamOf.get(y.driverId)) continue;
        const bond = bondOf(x.driverId, y.driverId, s.seasonNumber);
        bond.warmth += 2 / Gb;
        bond.sideBySide += 1;
      }

      if (input.measure) {
        closeSpans(leagueIndex);
        let boiling = 0;
        let hot = 0;
        let simmering = 0;
        for (const p of pairs.values()) {
          const h = p.heat * G;
          if (h >= tuning.boiling) boiling += 1;
          else if (h >= tuning.hot) hot += 1;
          else if (h >= tuning.simmering) simmering += 1;
        }
        timeline.push({ seasonNumber: s.seasonNumber, raceNumber: run.raceNumber, boiling, hot, simmering });
      }
      asOf = { seasonNumber: s.seasonNumber, raceNumber: run.raceNumber };
    }
  }
  if (input.measure) {
    for (const p of pairs.values()) {
      if (p.spanFrom !== null) spans.push({ a: p.a, b: p.b, from: p.spanFrom, to: leagueIndex, peak: Math.round(p.peak) });
    }
  }

  // ---- who is still here, who shares a shop now ----------------------------
  const latest = lastSeason;
  const activeNow = new Set(
    latest ? (!cutInside && latest.complete !== false && latest.offseason ? latest.offseason.roster : latest.roster).map((c) => c.driverId) : [],
  );
  const shopNow = new Map<string, string>();
  for (const t of latest?.teams ?? []) for (const id of t.driverIds) shopNow.set(id, normTeam(t.teamId));

  // ---- head to head: every driver's finish in every race, as one row each ----
  const raceCount = leagueIndex;
  const finishRow = new Map<string, Uint8Array>();
  {
    let i = 0;
    done: for (const s of seasons) {
      if (through && s.seasonNumber > through.seasonNumber) break;
      for (const run of [...s.season.races].sort((x, y) => x.raceNumber - y.raceNumber)) {
        if (through && s.seasonNumber === through.seasonNumber && run.raceNumber > through.raceNumber) break done;
        for (const f of run.race.finishingOrder) {
          let row = finishRow.get(f.driverId);
          if (!row) {
            row = new Uint8Array(raceCount);
            finishRow.set(f.driverId, row);
          }
          row[i] = Math.min(255, f.place);
        }
        i += 1;
      }
    }
  }

  const momentsByRace = new Map<string, RivalryMoment[]>();
  const records = new Map<string, RivalryRecord>();
  for (const [k, p] of pairs) {
    if (p.moments.length === 0) continue;
    const h2h: HeadToHead = { races: 0, aAhead: 0, bAhead: 0 };
    const ra = finishRow.get(p.a);
    const rb = finishRow.get(p.b);
    if (ra && rb) {
      for (let i = 0; i < raceCount; i++) {
        const x = ra[i];
        const y = rb[i];
        if (x === 0 || y === 0) continue;
        h2h.races += 1;
        if (x < y) h2h.aAhead += 1;
        else h2h.bAhead += 1;
      }
    }
    for (const m of p.moments) {
      const rk = `${m.seasonNumber}|${m.raceNumber}`;
      const list = momentsByRace.get(rk);
      if (list) list.push(m);
      else momentsByRace.set(rk, [m]);
    }
    const heatNow = p.heat * G;
    const bothActive = activeNow.has(p.a) && activeNow.has(p.b);
    const heated = p.moments.filter((m) => m.kind !== 'hatchet' && m.heat > 0);
    const firstSpark = heated[0] ?? null;
    const worst = heated.reduce<RivalryMoment | null>((w, m) => (!w || m.heat > w.heat ? m : w), null);
    const lastHot = heated[heated.length - 1];
    const buried = Boolean(p.hatchet && (!lastHot || lastHot.leagueRaceIndex <= p.hatchet.leagueRaceIndex));
    const state: RivalryStateName = !bothActive
      ? p.peak >= tuning.hot
        ? 'cooled'
        : 'dormant'
      : buried && heatNow < tuning.hot
        ? 'buried-the-hatchet'
        : heatNow >= tuning.boiling
          ? 'boiling'
          : heatNow >= tuning.hot
            ? 'hot'
            : heatNow >= tuning.simmering
              ? 'simmering'
              : p.peak >= tuning.hot
                ? 'cooled'
                : 'dormant';
    // Rising: real heat added in the last three races. Falling: nothing in six.
    const recent = heated.filter((m) => m.leagueRaceIndex > leagueIndex - 3).reduce((t, m) => t + m.heat, 0);
    const trend: RivalryRecord['trend'] =
      recent >= 2 ? 'rising' : !lastHot || lastHot.leagueRaceIndex <= leagueIndex - 6 ? 'falling' : 'steady';
    records.set(k, {
      a: p.a,
      b: p.b,
      aName: name(p.a),
      bName: name(p.b),
      heat: Math.round(heatNow * 10) / 10,
      peak: Math.round(p.peak * 10) / 10,
      peakAt: p.peakAt,
      state,
      firstSpark,
      worst,
      moments: p.moments,
      hatchet: p.hatchet,
      headToHead: h2h,
      bothActive,
      teammates: Boolean(shopNow.get(p.a) && shopNow.get(p.a) === shopNow.get(p.b)),
      trend,
    });
  }

  const rivalries = [...records.values()]
    .filter((r) => r.state !== 'dormant' || r.peak >= tuning.simmering)
    .sort((x, y) => STATE_RANK[x.state] - STATE_RANK[y.state] || y.heat - x.heat || y.peak - x.peak || x.a.localeCompare(y.a));

  const bondList: Bond[] = [];
  for (const b of bonds.values()) {
    const current = Boolean(shopNow.get(b.a) && shopNow.get(b.a) === shopNow.get(b.b)) && activeNow.has(b.a) && activeNow.has(b.b);
    const seasonsTogether = [...b.seasons].sort((x, y) => x - y);
    const warmth = b.warmth * Gb;
    const kind: BondKind | null =
      b.mentorId && (current || seasonsTogether.length >= 2)
        ? 'mentor'
        : warmth >= 6 || b.sideBySide >= 3
          ? 'friends'
          : seasonsTogether.length >= 3
            ? 'teammates'
            : null;
    if (!kind) continue;
    // A pair at war is not a friendship, whatever the shop says.
    const war = records.get(pairKey(b.a, b.b));
    if (war && (war.state === 'hot' || war.state === 'boiling')) continue;
    bondList.push({
      a: b.a,
      b: b.b,
      aName: name(b.a),
      bName: name(b.b),
      kind,
      mentorId: b.mentorId,
      warmth: Math.round(warmth * 10) / 10,
      seasonsTogether,
      sideBySide: b.sideBySide,
      since: b.since,
      current,
    });
  }
  bondList.sort((x, y) => Number(y.current) - Number(x.current) || y.warmth - x.warmth || y.sideBySide - x.sideBySide || x.a.localeCompare(y.a));

  return {
    asOf,
    rivalries,
    bonds: bondList,
    byPair: records,
    momentsByRace,
    ...(input.measure ? { timeline, spans } : {}),
  };
}

const AGGRESSIVE: ReadonlySet<IncidentKind> = new Set<IncidentKind>([
  'wrecked',
  'duel',
  'duel-failed',
  'feud-flare',
  'lead-pass',
  'photo-finish',
  'late-pass-win',
  'seat',
]);

const STATE_RANK: Record<RivalryStateName, number> = {
  boiling: 0,
  hot: 1,
  simmering: 2,
  'buried-the-hatchet': 3,
  cooled: 4,
  dormant: 5,
};

/**
 * A veteran and a young man in one shop: the veteran's id.
 *
 * The winter signs rookies as independents (they do not take shop seats), so
 * "rookie" here is the young driver a shop already has: 25 or under, or in his
 * first two seasons of a league past its first. The veteran is 36 or older, or
 * four seasons in, and at least ten years the elder. Ages are the card's
 * (`aging.ts`); nothing is invented.
 */
function veteranRookie(x: string, y: string, s: HistorySeason, seen: ReadonlyMap<string, number>): string | null {
  const card = (id: string) => s.roster.find((c) => c.driverId === id);
  const age = (id: string) => {
    const c = card(id);
    return c ? ageOf(c) : null;
  };
  const isYoung = (id: string) => {
    const a = age(id);
    return (a !== null && a <= 25) || (s.seasonNumber > 1 && (seen.get(id) ?? 0) <= 1);
  };
  const isVet = (id: string) => {
    const a = age(id);
    return (seen.get(id) ?? 0) >= 4 || (a !== null && a >= 36);
  };
  const gapOk = (v: string, r: string) => {
    const av = age(v);
    const ar = age(r);
    return av !== null && ar !== null && av - ar >= 10;
  };
  if (isVet(x) && isYoung(y) && gapOk(x, y)) return x;
  if (isVet(y) && isYoung(x) && gapOk(y, x)) return y;
  return null;
}

/**
 * The incidents a race's RESULT holds on its own — they survive the archive:
 * a finish decided at the line, and a late pass for the win.
 */
export function finishIncidents(run: SeasonRaceResult): Incident[] {
  const out: Incident[] = [];
  const race = run.race;
  for (const at of race.photoFinish?.attempts ?? []) {
    if (at.momentTag === 'pfa.won-the-race') out.push({ k: 'photo-finish', a: at.attackerId, b: at.defenderId, t: race.turnsRun, d: 'win' });
    else if (at.momentTag === 'pfa.defended' && at.fromPosition === 2) out.push({ k: 'photo-finish', a: at.defenderId, b: at.attackerId, t: race.turnsRun, d: 'held' });
    else if (at.momentTag === 'pfa.passed') out.push({ k: 'photo-finish', a: at.attackerId, b: at.defenderId, t: race.turnsRun, d: String(at.toPosition) });
  }
  const changes = race.leadChanges;
  const last = changes[changes.length - 1];
  if (last && race.winnerId && last.driverId === race.winnerId && race.turnsRun > 0 && last.turn >= race.turnsRun * 0.85) {
    const prev = changes.length >= 2 ? changes[changes.length - 2].driverId : (race.startingOrder?.[0] ?? null);
    const prevFinish = prev ? race.finishingOrder.find((f) => f.driverId === prev) : undefined;
    const decidedAtLine = out.some((i) => i.d === 'win' || i.d === 'held');
    if (prev && prev !== last.driverId && prevFinish && prevFinish.place <= 3 && prevFinish.status === 'running' && !decidedAtLine) {
      out.push({ k: 'late-pass-win', a: last.driverId, b: prev, t: last.turn });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Reading the book
// ---------------------------------------------------------------------------

/** The record between two drivers, or null when the dice never put them together. */
export function rivalryBetween(book: RivalryBook, a: string, b: string): RivalryRecord | null {
  return book.byPair.get(pairKey(a, b)) ?? null;
}

/** The hottest rivalries between drivers still racing, hottest first. */
export function hottestRivalries(book: RivalryBook, n = 8): RivalryRecord[] {
  return book.rivalries.filter((r) => r.state === 'boiling' || r.state === 'hot' || r.state === 'simmering').slice(0, n);
}

export interface Relationships {
  driverId: string;
  /** His rivals, hottest first (any state but dormant). */
  rivals: { other: string; otherName: string; record: RivalryRecord }[];
  /** Friends and teammates he gets on with. */
  friends: { other: string; otherName: string; bond: Bond }[];
  /** The veteran who brought him along, if any. */
  mentor: { id: string; name: string; bond: Bond } | null;
  /** The rookies he brought along. */
  proteges: { id: string; name: string; bond: Bond }[];
}

export function relationshipsOf(book: RivalryBook, driverId: string): Relationships {
  const rivals = book.rivalries
    .filter((r) => (r.a === driverId || r.b === driverId) && r.state !== 'dormant')
    .map((r) => ({ other: r.a === driverId ? r.b : r.a, otherName: r.a === driverId ? r.bName : r.aName, record: r }));
  const mine = book.bonds.filter((b) => b.a === driverId || b.b === driverId);
  const other = (b: Bond) => (b.a === driverId ? { id: b.b, name: b.bName } : { id: b.a, name: b.aName });
  const mentorBond = mine.find((b) => b.kind === 'mentor' && b.mentorId !== driverId);
  return {
    driverId,
    rivals,
    friends: mine.filter((b) => b.kind !== 'mentor').map((b) => ({ other: other(b).id, otherName: other(b).name, bond: b })),
    mentor: mentorBond ? { ...other(mentorBond), bond: mentorBond } : null,
    proteges: mine.filter((b) => b.kind === 'mentor' && b.mentorId === driverId).map((b) => ({ ...other(b), bond: b })),
  };
}

/** Rival pairs running within `within` places of each other in `order` (ids, leader first). */
export function rivalsNear(
  order: readonly string[],
  book: RivalryBook,
  within = 3,
): { a: string; b: string; gap: number; record: RivalryRecord }[] {
  const pos = new Map(order.map((id, i) => [id, i]));
  const out: { a: string; b: string; gap: number; record: RivalryRecord }[] = [];
  for (const r of hottestRivalries(book, 40)) {
    const x = pos.get(r.a);
    const y = pos.get(r.b);
    if (x === undefined || y === undefined) continue;
    const gap = Math.abs(x - y);
    if (gap <= within) out.push({ a: x < y ? r.a : r.b, b: x < y ? r.b : r.a, gap, record: r });
  }
  return out.sort((p, q) => q.record.heat - p.record.heat);
}

/** The words for a state, as a board prints them. */
export const STATE_WORDS: Record<RivalryStateName, string> = {
  boiling: 'Boiling',
  hot: 'Hot',
  simmering: 'Simmering',
  cooled: 'Cooled',
  'buried-the-hatchet': 'Buried the hatchet',
  dormant: 'Quiet',
};

export type { SeasonRaceResult };
